import React, { useState } from "react";
import { Card, Form, Row, Col, Button, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { sendRegisterOtp, registerPartner } from "./services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;

const CATEGORY_OPTIONS = [
  { value: "TravelAgency", label: "Travel Agency" },
  { value: "Hotel", label: "Hotel" },
  { value: "CabProvider", label: "Cab / Taxi Provider" },
  { value: "TourOperator", label: "Tour Operator" },
  { value: "Other", label: "Other Travel Business" },
];

export default function PartnerRegister() {
  const [step, setStep] = useState(1); // 1 = details + send OTP, 2 = verify OTP
  const [form, setForm] = useState({
    businessName: "", ownerName: "", email: "", mobile: "",
    category: "TravelAgency", city: "", address: "", password: "",
    serviceDestinations: "",
  });
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (!form.businessName || !form.email || !form.mobile || !form.password) {
      return setError("Business name, email, mobile and password are required");
    }
    setLoading(true);
    try {
      await sendRegisterOtp(form.email.trim().toLowerCase());
      setInfo("OTP sent to your email");
      setStep(2);
    } catch (err) {
      setError(getErr(err, "Failed to send OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!otp) return setError("Please enter the OTP sent to your email");

    setLoading(true);
    try {
      const { data } = await registerPartner({
        ...form,
        email: form.email.trim().toLowerCase(),
        otp,
      });
      localStorage.setItem("partner_token", data.token);
      localStorage.setItem("partner_profile", JSON.stringify(data.data));
      navigate("/partner/dashboard");
    } catch (err) {
      setError(getErr(err, "Registration failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center agent-login-container py-5">
      <Card className="agent-login-card w-100" style={{ maxWidth: "620px" }}>
        <Row>
          <Col className="agent-login-right-panel px-4 py-4">
            <div className="text-center mb-3">
              <h2 className="fw-bold text-light">Join Bachatwali Ticket as a Travel Partner</h2>
              <p className="text-light mb-0">Free registration — list your business and receive qualified customer requests.</p>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            {info && <Alert variant="success">{info}</Alert>}

            {step === 1 && (
              <Form onSubmit={handleSendOtp}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Business Name</Form.Label>
                      <Form.Control value={form.businessName} onChange={update("businessName")} required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Owner / Contact Person</Form.Label>
                      <Form.Control value={form.ownerName} onChange={update("ownerName")} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Business Email</Form.Label>
                      <Form.Control type="email" value={form.email} onChange={update("email")} required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Business Mobile</Form.Label>
                      <Form.Control value={form.mobile} onChange={update("mobile")} required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Business Type</Form.Label>
                      <Form.Select value={form.category} onChange={update("category")}>
                        {CATEGORY_OPTIONS.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">City</Form.Label>
                      <Form.Control value={form.city} onChange={update("city")} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Destinations You Serve (comma separated)</Form.Label>
                      <Form.Control placeholder="e.g. Goa, Manali, Jaipur" value={form.serviceDestinations} onChange={update("serviceDestinations")} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Password</Form.Label>
                      <Form.Control type="password" value={form.password} onChange={update("password")} required />
                    </Form.Group>
                  </Col>
                </Row>
                <Button type="submit" className="w-100 login-btn mb-2" disabled={loading}>
                  {loading ? "Sending OTP…" : "Send OTP to Email"}
                </Button>
              </Form>
            )}

            {step === 2 && (
              <Form onSubmit={handleRegister}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light">Enter the OTP sent to {form.email}</Form.Label>
                  <Form.Control value={otp} onChange={(e) => setOtp(e.target.value)} required />
                </Form.Group>
                <Button type="submit" className="w-100 login-btn mb-2" disabled={loading}>
                  {loading ? "Verifying…" : "Verify & Create Account"}
                </Button>
                <Button variant="link" className="w-100 text-light" onClick={() => setStep(1)}>
                  Back
                </Button>
              </Form>
            )}

            <div className="text-center mt-3">
              <span className="text-light">
                Already a partner? <Link to="/partner/login">Login</Link>
              </span>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
